import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

const ElementsSelector = ({
    elementsCategoriesList = [],
    selectedElementIds = [],
    onToggleElement
}) => {
    const { t } = useTranslation('common');
    const [showDropdown, setShowDropdown] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [expandedCategories, setExpandedCategories] = useState([]);
    const dropdownRef = useRef(null);

    const safeCategories = useMemo(() => {
        return Array.isArray(elementsCategoriesList) ? elementsCategoriesList : [];
    }, [elementsCategoriesList]);

    // Filter elements and categories by search term
    const filteredCategories = useMemo(() => {
        if (!searchTerm.trim()) return safeCategories;
        const term = searchTerm.trim().toLowerCase();
        return safeCategories
            .map((cat) => {
                const matchedElems = (cat.Elements || []).filter((e) =>
                    (e.ElementName || '').toLowerCase().includes(term)
                );
                return { ...cat, Elements: matchedElems };
            })
            .filter((cat) => cat.Elements.length > 0);
    }, [safeCategories, searchTerm]);

    // Group selected elements by category
    const { selectedCategoriesList, totalSelectedCount } = useMemo(() => {
        const categoriesList = [];
        let count = 0;
        const selectedSet = new Set((selectedElementIds || []).map(Number));

        safeCategories.forEach((cat) => {
            const matched = (cat.Elements || []).filter((elem) =>
                selectedSet.has(elem.ElementId)
            );
            if (matched.length > 0) {
                count += matched.length;
                categoriesList.push({
                    CategoryId: cat.CategoryId,
                    CategoryName: cat.CategoryName,
                    Elements: matched
                });
            }
        });

        return {
            selectedCategoriesList: categoriesList,
            totalSelectedCount: count
        };
    }, [safeCategories, selectedElementIds]);

    // Handle click outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const toggleCategoryExpand = (catId) => {
        setExpandedCategories((prev) =>
            prev.includes(catId) ? prev.filter((id) => id !== catId) : [...prev, catId]
        );
    };

    return (
        <div className="modern-form-section h-100 mb-0">
            <div className="modern-section-title text-danger font-weight-bold d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center">
                    <i className="icon fal fa-cube ml-2 text-danger"></i>
                    <span>{t('QuestionView.elementsSection') || 'عناصر السؤال'}</span>
                </div>
                <span className="badge badge-pill badge-light text-muted small font-weight-normal border px-2 py-1">
                    {t('QuestionView.sameCategoryElementsNote') || '(اختيار متعدد)'}
                </span>
            </div>

            <div className="form-group w-100 mb-0">
                <div className="modern-tree-select-wrapper" ref={dropdownRef}>
                    <div
                        className="modern-tree-select-trigger"
                        onClick={() => setShowDropdown((prev) => !prev)}
                    >
                        <div className="modern-tree-select-values">
                            {totalSelectedCount > 0 ? (
                                <span className="font-weight-bold text-dark">
                                    {`${t('QuestionView.selectedElementsCount') || 'العناصر المختارة'} (${totalSelectedCount})`}
                                </span>
                            ) : (
                                <span className="text-muted">
                                    {t('QuestionView.selectElements') || 'اختر العناصر...'}
                                </span>
                            )}
                        </div>
                        <i className={`fad fa-chevron-down arrow-icon ${showDropdown ? 'open' : ''}`}></i>
                    </div>

                    {showDropdown && (
                        <div className="modern-tree-select-dropdown">
                            <div className="modern-tree-search-wrapper">
                                <input
                                    type="text"
                                    className="form-control modern-search-input"
                                    placeholder={t('QuestionView.searchElements') || 'بحث في العناصر...'}
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    onClick={(e) => e.stopPropagation()}
                                />
                                <i className="fal fa-search search-icon"></i>
                            </div>

                            <div className="modern-tree-nodes">
                                {filteredCategories.map((cat) => {
                                    const isExpanded = searchTerm.trim() ? true : expandedCategories.includes(cat.CategoryId);

                                    return (
                                        <div key={cat.CategoryId} className="modern-tree-category">
                                            <div
                                                className="modern-category-header d-flex align-items-center justify-content-between"
                                                onClick={() => toggleCategoryExpand(cat.CategoryId)}
                                            >
                                                <div className="d-flex align-items-center gap-2">
                                                    <i
                                                        className={`far ${isExpanded ? 'fa-folder-open' : 'fa-folder'} category-folder-icon`}
                                                    ></i>
                                                    <span className="category-name">{cat.CategoryName}</span>
                                                </div>
                                                <div className="d-flex align-items-center gap-1">
                                                    <span className="category-badge">
                                                        {cat.Elements.length}
                                                    </span>
                                                    <i
                                                        className={`fal fa-chevron-down category-chevron ${
                                                            isExpanded ? 'expanded' : ''
                                                        }`}
                                                    ></i>
                                                </div>
                                            </div>

                                            {isExpanded && (
                                                <div className="modern-category-children">
                                                    {cat.Elements.map((elem) => {
                                                        const isChecked = selectedElementIds.includes(elem.ElementId);
                                                        return (
                                                            <label
                                                                key={elem.ElementId}
                                                                className="modern-tree-item"
                                                                onClick={(e) => e.stopPropagation()}
                                                            >
                                                                <input
                                                                    type="checkbox"
                                                                    checked={isChecked}
                                                                    onChange={() =>
                                                                        onToggleElement(elem.ElementId, cat.CategoryId)
                                                                    }
                                                                />
                                                                <span className="checkbox-custom"></span>
                                                                <span className="topic-name">{elem.ElementName}</span>
                                                            </label>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}

                                {filteredCategories.length === 0 && (
                                    <div className="text-center text-muted p-2 small">
                                        {t('QuestionView.noMatchingElements') || 'لا توجد نتائج مطابقة'}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {selectedCategoriesList.length > 0 && (
                    <div className="selected-topics-tags-list mt-3">
                        <div className="selected-topics-label text-danger">
                            <i className="far fa-cube ml-1"></i>
                            {t('QuestionView.selectedElementsCount') || 'العناصر المختارة:'}
                        </div>
                        <div className="selected-categories-container">
                            {selectedCategoriesList.map((cat) => (
                                <div key={cat.CategoryId} className="selected-category-group mb-2">
                                    <div className="selected-category-header-title text-danger">
                                        <i className="far fa-folder-open ml-1 text-danger"></i>
                                        <span className="category-group-name">{cat.CategoryName}</span>
                                        <span className="selected-category-badge">({cat.Elements.length})</span>
                                    </div>
                                    <div className="selected-tags-container">
                                        {cat.Elements.map((elem) => (
                                            <div
                                                key={elem.ElementId}
                                                className="selected-topic-chip"
                                                style={{ background: '#fee2e2', borderColor: '#fca5a5', color: '#991b1b' }}
                                            >
                                                <i className="far fa-tag topic-chip-icon text-danger"></i>
                                                <span className="topic-chip-name">{elem.ElementName}</span>
                                                <button
                                                    type="button"
                                                    className="topic-chip-remove"
                                                    title={t('Cancel') || 'حذف'}
                                                    onClick={() => onToggleElement(elem.ElementId, cat.CategoryId)}
                                                >
                                                    <i className="far fa-times text-danger"></i>
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default React.memo(ElementsSelector);
